import { DataGrid } from '@tableau/data-grid-1-wrapper';
import React from 'react';
import { defaultMemoize } from 'reselect/lib';
import _ from 'underscore';

import { Order } from '../../../../ts/helpers/Order';
import { ISliceable } from '../../../../ts/models/ISliceable';
import { IOrderClause } from '../../../../ts/ServerApi/types/IOrderClause';
import { CommonStyles } from '../../CommonStyles';
import { SparseCachedList } from '../../SparseCachedList';

/*
 * All usages of this file should be migrated over to use SortableDataGrid3.tsx
 * in order to consume the newer widgets-datagrid version 3.
 */

export type SortableDataGridProps<T> = {
  readonly columns: DataGrid.Column[];
  readonly getSliceable: (orderClause: ReadonlyArray<IOrderClause>) => ISliceable<T>;
  readonly getRowFromData?: (rowData: T) => DataGrid.Row;
  readonly initialColumnOrders?: ReadonlyArray<IOrderClause>;
  readonly emptyChild?: React.ReactChild;
  readonly dataGridProps?: Partial<DataGrid.Props>; // Configure or override props for the datagrid
};

export type SortableDataGridState = {
  readonly columnOrders: ReadonlyArray<IOrderClause>;
};

export function getHeightForRows(numRows: number): number {
  return numRows * CommonStyles.Sizes.DataGridRowHeight + 2;
}

function createRowStore<T>(
  getSliceable: (columnOrders: ReadonlyArray<IOrderClause>) => ISliceable<T>,
  getRowFromData: (rowData: T) => DataGrid.Row,
  columnOrders: ReadonlyArray<IOrderClause>
): DataGrid.RowStore {

  const rowStore: SparseCachedList<DataGrid.Row, T> = new SparseCachedList(
    getSliceable(columnOrders), getRowFromData
  );

  return {
    getItems: (start: number, end: number, rowHandler: (rows: DataGrid.Row[]) => void) => {
      rowStore.getItems(start, end).then(rowHandler);
    }
  };
}

function defaultRowStructure(rowData: any): DataGrid.Row {
  return rowData && { key: rowData.id, data: rowData };
}

const datagridConfig: DataGrid.Config = {
  rememberLastScrollPos: true,
};

export class SortableDataGrid<T> extends React.PureComponent<SortableDataGridProps<T>, SortableDataGridState> {

  private memoizedCreateRowStore = defaultMemoize(createRowStore);

  constructor(props: SortableDataGridProps<T>) {
    super(props);
    const columnOrders = this.props.initialColumnOrders || [];
    this.state = {
      columnOrders: columnOrders
    };
  }

  public render(): JSX.Element {
    const rowStore = this.memoizedCreateRowStore(
      this.props.getSliceable,
      this.props.getRowFromData ? this.props.getRowFromData : defaultRowStructure,
      this.state.columnOrders
    );

    const currentSortedColumn = this.state.columnOrders[0];

    return (
      <DataGrid
        {...this.props.dataGridProps}
        emptyChild={this.props.emptyChild}
        cols={this.props.columns}
        rowStore={rowStore}
        onColumnHeaderClick={this.onColumnHeaderClicked}
        sortKey={currentSortedColumn && currentSortedColumn.field}
        sortAscending={currentSortedColumn && currentSortedColumn.ascending}
        config={datagridConfig}
      />
    );
  }

  private readonly onColumnHeaderClicked = (columnInfo: DataGrid.ColumnInfo, e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const isSortableColumn: boolean = this.props.columns.find(column => column.dataKey === columnInfo.columnKey).sortable;
    if (!isSortableColumn) {
      return;
    }
    const orders = this.getNewColumnOrdering(columnInfo.columnKey);
    this.setState({columnOrders: orders});
  }

  private readonly getNewColumnOrdering = (field: string): IOrderClause[]  => {
    const existingClause = Order.findOrderClauseForField(this.state.columnOrders, field);
    let newOrder: IOrderClause[];

    if (existingClause) {
      if (Order.startsWithField(this.state.columnOrders, field)) {
        // If the field is already the primary sort order, toggle the direction (asc/desc).
        newOrder = Order.withFirstClauseToggled(this.state.columnOrders);
      } else {
        // If the field is used but not primary, move it up to and dont toggle the direction (asc/desc).
        newOrder = _.reject(this.state.columnOrders, clause => clause.field === field);
        newOrder = Order.withClausePrepended(newOrder, existingClause);
      }
    } else {
      // If the field is not used at all currently, add it as the primary order clause using column definition
      const defaultDirection = this.props.columns.find(column => column.dataKey === field).direction === 'asc';
      const newClause: IOrderClause = { field: field, ascending: defaultDirection };
      newOrder = Order.withClausePrepended(this.state.columnOrders, newClause);
    }
    return newOrder;
  }
}
